import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Select } from "chakra-react-select";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import PasswordInput from "../Forms/PasswordInput";
import { IoMdArrowRoundBack } from "react-icons/io";

const WhatsAppModal = ({
  isOpen,
  onClose,
  userId,
  loadUserWhatsAppDetails,
  data
}) => {
  const baseUrl = getHostDomain();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: undefined,
  });

  const [status, setStatus] = useState("idle");
  const loading = status === "loading";

  const [currentWhatsAppAcc, setCurrentWhatsAppAcc] = useState();
  const [creatingNewAccount, setCreatingNewAccount] = useState(false);

  const whatsappOptions =  data?.length && data?.map((acc) => ({
    value: acc,
    label: acc.whatsapp_phone_number || "WhatsApp Account",
  }));

  const onSubmitWhatsAppDetails = async (values) => {
    try {
      setStatus("loading");

      const { id, ...body } = values;

      const urlHit =
        baseUrl +
        (id ? `/api/v1/user/${id}/UserWhatsApp` : `/api/v1/user/UserWhatsApp`);

      await fetchController(urlHit, id ? "PUT" : "POST", body);

      toast.success(
        id ? "WhatsApp details updated" : "WhatsApp details created"
      );

      setCreatingNewAccount(false);
      reset();
      setCurrentWhatsAppAcc(undefined);
      onClose();
      loadUserWhatsAppDetails();
    } catch (error) {
      toast.error("An error occurred.");
    } finally {
      setStatus("idle");
    }
  };

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent height="auto" width="95%" maxWidth="600px">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(onSubmitWhatsAppDetails)(); }}>
          <ModalHeader>WhatsApp Account Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading && (
              <Center
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                background="rgba(255,255,255,0.8)"
                zIndex="999"
              >
                <Spinner />
              </Center>
            )}

            <Stack spacing={3}>
              {creatingNewAccount ? (
                <Flex justifyContent="flex-start">
                  <IoMdArrowRoundBack
                    size={24}
                    cursor="pointer"
                    onClick={() => {
                      setCreatingNewAccount(false);
                      reset();
                      setCurrentWhatsAppAcc(undefined);
                    }}
                  />
                </Flex>
              ) : (
                <FormControl>
                  <FormLabel>Select Existing WhatsApp Account</FormLabel>
                  <Select
                    value={currentWhatsAppAcc}
                    options={whatsappOptions}
                    isLoading={status === "accountsLoading"}
                    onChange={({ value, label }) => {
                      reset(value);
                      setCreatingNewAccount(false);
                      setCurrentWhatsAppAcc({ label, value });
                    }}
                    placeholder="Select WhatsApp Account"
                  />
                </FormControl>
              )}

              {(currentWhatsAppAcc || creatingNewAccount) && (
                <>
                  {!creatingNewAccount && <Divider />}

                  <FormControl isInvalid={errors.whatsapp_phone_number}>
                    <FormLabel>WhatsApp Phone Number</FormLabel>
                    <Input
                      type="number"
                      {...register("whatsapp_phone_number", { required: true })}
                      placeholder="WhatsApp Phone Number"
                    />
                    <FormErrorMessage>
                      {errors.whatsapp_phone_number?.message ||
                        "Field is required"}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.phone_number_id}>
                    <FormLabel>Phone Number ID</FormLabel>
                    <Input
                      {...register("phone_number_id", { required: true })}
                      placeholder="Phone Number ID"
                    />
                    <FormErrorMessage>
                      {errors.phone_number_id && "Phone Number ID is required"}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.phone_business_id}>
                    <FormLabel>Phone Business ID</FormLabel>
                    <Input
                      {...register("phone_business_id", { required: true })}
                      placeholder="Phone Business ID"
                    />
                    <FormErrorMessage>
                      {errors.phone_business_id &&
                        "Phone Business ID is required"}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.whatsapp_access_token}>
                    <FormLabel>WhatsApp Access Token</FormLabel>
                    <PasswordInput
                      placeholder="WhatsApp Access Token"
                      register={{
                        ...register("whatsapp_access_token", {
                          required: true,
                        }),
                      }}
                    />
                    <FormErrorMessage>
                      {errors.whatsapp_access_token &&
                        "Access Token is required"}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.facebook_app_secret}>
                    <FormLabel>Facebook App Secret</FormLabel>
                    <Input
                      {...register("facebook_app_secret", { required: true })}
                      placeholder="Facebook App Secret"
                    />
                    <FormErrorMessage>
                      {errors.facebook_app_secret &&
                        "Facebook App Secret is required"}
                    </FormErrorMessage>
                  </FormControl>

                  <Flex justifyContent="flex-start">
                    <Link
                      color="blue.500"
                      href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started"
                      isExternal
                    >
                      Get Keys <ExternalLinkIcon mx="2px" />
                    </Link>
                  </Flex>
                </>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Flex
              gap={2}
              width="100%"
              justifyContent="center"
              marginBlock={2}
              alignItems="center"
            >
              <Button isLoading={loading} type="button" onClick={() => handleSubmit(onSubmitWhatsAppDetails)()} colorScheme="blue">
                {currentWhatsAppAcc ? "Update" : "Submit"}
              </Button>

              {!creatingNewAccount && (
                <Button
                  onClick={() => {
                    setCreatingNewAccount(true);
                    reset({
                      whatsapp_phone_number: "",
                      phone_number_id: "",
                      phone_business_id: "",
                      whatsapp_access_token: "",
                      facebook_app_secret: "",
                    });
                    setCurrentWhatsAppAcc(undefined);
                  }}
                >
                  Create New
                </Button>
              )}
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default WhatsAppModal;
